import type { Community } from '@communities/community/domain/Community';
import { useCommunityHeaderV2 } from '@core/components';
import { CommunityAuditHeaderSection, AuditAnalyticsSection } from '@internal-audit/components/CommunityAuditHeaderSection';
import type { FlexibleAuditSession } from '@internal-audit/domain/graphql-types';
import { useAuditAnalyticsData } from '@internal-audit/hooks/useAuditAnalyticsData';

interface SessionsPageHeaderProps {
  community: Community | null;
  sessions: FlexibleAuditSession[];
  sessionsLoading: boolean;
}

/**
 * Header section component with hero background
 * Contains the community audit header
 * In V2: Returns both header and analytics section (to be rendered in page body)
 */
export function SessionsPageHeader({ community, sessions, sessionsLoading }: SessionsPageHeaderProps) {
  return (
    <div>
      <CommunityAuditHeaderSection auditSessions={sessions} community={community} isLoading={sessionsLoading} />
    </div>
  );
}

/**
 * Analytics section for page body (V2 layout)
 * Separated from header to enable the new design system header
 */
export function SessionsPageAnalytics({ community, sessions, sessionsLoading }: SessionsPageHeaderProps) {
  const isHeaderV2Enabled = useCommunityHeaderV2();

  // Fetch analytics data using the shared hook
  const {
    auditReports,
    comprehensiveAnalytics,
    isLoading: analyticsLoading,
  } = useAuditAnalyticsData({
    communityId: community?.id,
    skip: !isHeaderV2Enabled, // Only fetch if V2 is enabled
  });

  // Only render in V2 mode
  if (!isHeaderV2Enabled) {
    return null;
  }

  const isLoading = sessionsLoading || analyticsLoading;

  return <AuditAnalyticsSection auditReports={auditReports} auditSessions={sessions} comprehensiveAnalytics={comprehensiveAnalytics} isLoading={isLoading} />;
}
